import { useAuth } from '@/context/AuthContext';
import UserAvatarSVG from '@/components/UserAvatarSVG';
import { useState, useEffect } from 'react';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import '@/styles/SustainabilityDashStyles.scss';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import CO2Icon from '@/assets/Images/CO2 Icon.svg';
import KWhIcon from '@/assets/Images/Energy Icon.svg';
import H2oIcon from '@/assets/Images/H20 Icon.svg';
import TreeSavedIcon from '@/assets/Images/Tree Saved Icon.svg';
import SolarIcon from '@/assets/Images/Solar Icon.svg';
import EcoIcon from '@/assets/Images/Eco Icon.svg';
import * as Sentry from '@sentry/react';

const WASTE_COLORS = [
    "#800000",  // Paper - dark red
    "#F63040",  // General waste - coral/salmon
    "#FF0000",  // Plastic - red
    "#843C0C",  // Scrap metal - brown
    "#3B3838",  // Glass - gray
    "#767171",  // Hazardous dry - sienna/brown
    "#AFABAB"   // Tetrapack - light gray
];

const MANAGEMENT_COLORS = [
    "#F63040",  // Recycled (primary color)
    "#57565B"   // Disposed (secondary color)
];

const SustainabilityDashboard = () => {
    const { apiFetch, user } = useAuth();
    const [initialLoading, setInitialLoading] = useState(true);

    // Pie chart states
    const [wasteData, setWasteData] = useState([]);
    const [wasteLoading, setWasteLoading] = useState(true);
    const [wasteError, setWasteError] = useState('');
    const [managementData, setManagementData] = useState([]);
    const [managementLoading, setManagementLoading] = useState(true);
    const [managementError, setManagementError] = useState('');

    // Sustainability stats state
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(true);
    const [statsError, setStatsError] = useState('');

    // Solar projects table state
    const [solarStats, setSolarStats] = useState([]);
    const [solarLoading, setSolarLoading] = useState(true);
    const [allProperties, setAllProperties] = useState([]);
    const [propertiesLoading, setPropertiesLoading] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => {
            setInitialLoading(false);
        }, 3000);

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        const fetchWasteData = async () => {
            setWasteLoading(true);
            setWasteError('');
            try {
                const response = await apiFetch('/api/Sustainability/waste-types');
                const data = await response.json();
                setWasteData(
                    Array.isArray(data)
                        ? data.map((item) => ({
                            name: item.wasteType,
                            value: item.percentage
                        }))
                        : []
                );
            } catch (catchError) {
                Sentry.captureException(catchError);
                setWasteError('Failed to load waste data.');
            } finally {
                setWasteLoading(false);
            }
        };
        fetchWasteData();
    }, []);

    useEffect(() => {
        const fetchManagementData = async () => {
            setManagementLoading(true);
            setManagementError('');
            try {
                const response = await apiFetch('/api/Sustainability/waste-management-stats');
                const data = await response.json();
                setManagementData([
                    { name: 'Recycled', value: data.percentRecycled || 0 },
                    { name: 'Disposed', value: data.percentDisposed || 0 }
                ]);
            } catch (catchError) {
                Sentry.captureException(catchError);
                setManagementError('Failed to load waste management stats.');
            } finally {
                setManagementLoading(false);
            }
        };
        fetchManagementData();
    }, []);

    useEffect(() => {
        const fetchStats = async () => {
            setStatsLoading(true);
            setStatsError('');
            try {
                const response = await apiFetch('/api/Sustainability/sustainability-stats');
                const data = await response.json();
                setStats(data);
            } catch (catchError) {
                Sentry.captureException(catchError);
                setStatsError('Failed to load portfolio stats.');
            } finally {
                setStatsLoading(false);
            }
        };
        fetchStats();
    }, []);

    // Helper for formatting numbers with thousands separator
    const formatNum = (num) => num != null ? num.toLocaleString() : '0';

    // Fetch all solar project stats
    useEffect(() => {
        const fetchSolarStats = async () => {
            setSolarLoading(true);
            try {
                const response = await apiFetch('/api/Sustainability/solar-project-stats/all');
                const data = await response.json();
                setSolarStats(Array.isArray(data) ? data : []);
            } catch (catchError) {
                Sentry.captureException(catchError);
                // Optionally handle error
            } finally {
                setSolarLoading(false);
            }
        };
        fetchSolarStats();
    }, [apiFetch]);

    // Fetch all properties for property name lookup
    useEffect(() => {
        const fetchAllProperties = async () => {
            setPropertiesLoading(true);
            try {
                const response = await apiFetch('/api/Property/all');
                const data = await response.json();
                if (data.success && data.data) {
                    setAllProperties(data.data);
                }
            } catch (catchError) {
                Sentry.captureException(catchError);
                // Optionally handle error
            } finally {
                setPropertiesLoading(false);
            }
        };
        fetchAllProperties();
    }, [apiFetch]);

    // Map propertyID to propertyName
    const propertyIdToName = {};
    allProperties.forEach(p => {
        propertyIdToName[p.propertyID] = p.propertyName;
    });

    return (
        <>
            {initialLoading ? (
                <HexagonLoader />
            ) : (
                <div className="sustain-main-container">
                    <div className="leasing-dashboard-header">
                        <h1 className="dashboard-greeting">
                            <UserAvatarSVG />
                            Hello {user?.FirstName || user?.firstName}
                        </h1>
                        <h3>Sustainability Dashboard</h3>
                    </div>
                    <div className="sustain-top-row">
                        <div className="sustain-left-block">
                            <div className="sustain-block-header">Portfolio Statistics to Date (Cumulative)</div>
                            <div className="sustain-block-content">
                                <div className="sustain-left-block-grid">
                                    {/* CO2 and TreesGrown */}
                                    <div className="sustain-stat-block">
                                        {statsLoading ? (
                                            <span>Loading...</span>
                                        ) : statsError ? (
                                            <span className="error-message">{statsError}</span>
                                        ) : (
                                            <div className="sustain-stat-content">
                                                <div className="sustain-stat-icon-wrapper">
                                                    <img src={CO2Icon} alt="CO2 icon" className="sustain-stat-icon" />
                                                </div>
                                                <div className="sustain-stat-text">
                                                    <div className="sustain-stat-value">{formatNum(stats.cO2)}</div>
                                                    <div>CO2 saved (m<sup>3</sup>)</div>
                                                    <div className="sustain-stat-subtext">
                                                        <span className="sustain-stat-subvalue">{formatNum(stats.treesGrown)}</span> trees grown to maturity would have been required to sink this carbon.
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {/* KWHSaved and HomesPowered */}
                                    <div className="sustain-stat-block">
                                        {statsLoading ? (
                                            <span>Loading...</span>
                                        ) : statsError ? (
                                            <span className="error-message">{statsError}</span>
                                        ) : (
                                            <div className="sustain-stat-content">
                                                <div className="sustain-stat-icon-wrapper">
                                                    <img src={KWhIcon} alt="Energy icon" className="sustain-stat-icon" />
                                                </div>
                                                <div className="sustain-stat-text">
                                                    <div className="sustain-stat-value">{formatNum(stats.kwhSaved)}</div>
                                                    <div>kWh of energy saved</div>
                                                    <div className="sustain-stat-subtext">
                                                        <span className="sustain-stat-subvalue">{formatNum(stats.homesPowered)}</span> mid-income homes could be powered for one year by the energy saving.
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {/* LitersSaved and HomesWater */}
                                    <div className="sustain-stat-block">
                                        {statsLoading ? (
                                            <span>Loading...</span>
                                        ) : statsError ? (
                                            <span className="error-message">{statsError}</span>
                                        ) : (
                                            <div className="sustain-stat-content">
                                                <div className="sustain-stat-icon-wrapper">
                                                    <img src={H2oIcon} alt="Water icon" className="sustain-stat-icon" />
                                                </div>
                                                <div className="sustain-stat-text">
                                                    <div className="sustain-stat-value">{formatNum(stats.litersSaved)}</div>
                                                    <div>litres of water saved</div>
                                                    <div className="sustain-stat-subtext">
                                                        <span className="sustain-stat-subvalue">{formatNum(stats.homesWater)}</span> average households annual water needs.
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {/* TreesSaved and TropicalForest */}
                                    <div className="sustain-stat-block">
                                        {statsLoading ? (
                                            <span>Loading...</span>
                                        ) : statsError ? (
                                            <span className="error-message">{statsError}</span>
                                        ) : (
                                            <div className="sustain-stat-content">
                                                <div className="sustain-stat-icon-wrapper">
                                                    <img src={TreeSavedIcon} alt="Tree icon" className="sustain-stat-icon" />
                                                </div>
                                                <div className="sustain-stat-text">
                                                    <div className="sustain-stat-value">{formatNum(stats.treesSaved)}</div>
                                                    <div>trees saved</div>
                                                    <div className="sustain-stat-subtext">
                                                        <span className="sustain-stat-subvalue">{formatNum(stats.tropicalForest)}</span> acres of tropical forest at average tree density saved.
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="sustain-right-block">
                            <div className="sustain-block-header">SMART Monitoring</div>
                            <div className="sustain-block-content">
                                <div className="sustain-right-block-grid">
                                    {/* SMARTELEC */}
                                    <div className="sustain-stat-block">
                                        {statsLoading ? (
                                            <span>Loading...</span>
                                        ) : statsError ? (
                                            <span className="error-message">{statsError}</span>
                                        ) : (
                                            <div className="sustain-stat-content">
                                                <div className="sustain-stat-icon-wrapper">
                                                    <img src={SolarIcon} alt="Electricity meter icon" className="sustain-stat-icon" />
                                                </div>
                                                <div className="sustain-stat-text">
                                                    <div className="sustain-stat-value sustain-stat-percentage">{stats.smartelec != null ? `${stats.smartelec}%` : '0%'}</div>
                                                    <div>of buildings are SMART metered (electricity)</div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {/* SMARTBULK */}
                                    <div className="sustain-stat-block">
                                        {statsLoading ? (
                                            <span>Loading...</span>
                                        ) : statsError ? (
                                            <span className="error-message">{statsError}</span>
                                        ) : (
                                            <div className="sustain-stat-content">
                                                <div className="sustain-stat-icon-wrapper">
                                                    <img src={EcoIcon} alt="Eco icon" className="sustain-stat-icon" />
                                                </div>
                                                <div className="sustain-stat-text">
                                                    <div className="sustain-stat-value sustain-stat-percentage">{stats.smartbulk != null ? `${stats.smartbulk}%` : '0%'}</div>
                                                    <div>of buildings are SMART metered on bulk connections</div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {/* SMARTWATER */}
                                    <div className="sustain-stat-block">
                                        {statsLoading ? (
                                            <span>Loading...</span>
                                        ) : statsError ? (
                                            <span className="error-message">{statsError}</span>
                                        ) : (
                                            <div className="sustain-stat-content">
                                                <div className="sustain-stat-icon-wrapper">
                                                    <img src={H2oIcon} alt="Water meter icon" className="sustain-stat-icon" />
                                                </div>
                                                <div className="sustain-stat-text">
                                                    <div className="sustain-stat-value sustain-stat-percentage">{stats.smartwater != null ? `${stats.smartwater}%` : '0%'}</div>
                                                    <div>of buildings are SMART metered (water)</div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="sustain-bottom-row">
                        <div className="sustain-pie-block">
                            <div className="sustain-block-header">Waste Management</div>
                            <div className="sustain-pie-chart-placeholder" style={{ width: '100%', height: 320, marginTop: 8 }}>
                                {managementLoading ? (
                                    <div>Loading chart...</div>
                                ) : managementError ? (
                                    <div className="error-message">{managementError}</div>
                                ) : (
                                    <ResponsiveContainer width="100%" height={320}>
                                        <PieChart>
                                            <Pie
                                                data={managementData}
                                                dataKey="value"
                                                nameKey="name"
                                                cx="40%"
                                                cy="50%"
                                                innerRadius={50}
                                                outerRadius={90}
                                                label={({ value }) => `${value}%`}
                                                labelLine={true}
                                            >
                                                {managementData.map((entry, idx) => (
                                                    <Cell key={`cell-mgmt-${idx}`} fill={MANAGEMENT_COLORS[idx % MANAGEMENT_COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(value) => `${value}%`} />
                                            <Legend 
                                                verticalAlign="middle" 
                                                align="right"
                                                layout="vertical"
                                                wrapperStyle={{ right: '20%' }}
                                                formatter={(value) => <span style={{ color: '#333', fontSize: '0.85rem' }}>{value}</span>}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                )}
                            </div>
                        </div>
                        <div className="sustain-pie-block">
                            <div className="sustain-block-header">Types of Waste</div>
                            <div className="sustain-pie-chart-placeholder" style={{ width: '100%', height: 320, marginTop: 8 }}>
                                {wasteLoading ? (
                                    <div>Loading chart...</div>
                                ) : wasteError ? (
                                    <div className="error-message">{wasteError}</div>
                                ) : (
                                    <ResponsiveContainer width="100%" height={320}>
                                        <PieChart>
                                            <Pie
                                                data={wasteData}
                                                dataKey="value"
                                                nameKey="name"
                                                cx="40%"
                                                cy="50%"
                                                innerRadius={50}
                                                outerRadius={90}
                                                label={({ value }) => `${value}%`}
                                                labelLine={true}
                                            >
                                                {wasteData.map((entry, idx) => (
                                                    <Cell key={`cell-${idx}`} fill={WASTE_COLORS[idx % WASTE_COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(value) => `${value}%`} />
                                            <Legend 
                                                verticalAlign="middle" 
                                                align="right"
                                                layout="vertical"
                                                wrapperStyle={{ right: '20%' }}
                                                formatter={(value) => <span style={{ color: '#333', fontSize: '0.85rem' }}>{value}</span>}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                )}
                            </div>
                        </div>
                    </div>
                        {/* Solar Project Stats Table moved below pie charts */}
                        <div style={{ marginTop: '8px' }}>
                        {solarLoading || propertiesLoading ? (
                            <div>Loading solar project stats...</div>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', border: '1px solid #ccc' }}>
                                <thead>
                                    <tr>
                                        <th style={{ padding: '8px', borderBottom: '1px solid #ccc' }}>Solar Project Statistics</th>
                                        <th style={{ padding: '8px', borderBottom: '1px solid #ccc' }}>Plant Size (kW)</th>
                                        <th style={{ padding: '8px', borderBottom: '1px solid #ccc' }}>Yield (kWh/kW)</th>
                                        <th style={{ padding: '8px', borderBottom: '1px solid #ccc' }}>Expected Energy Generation (kWh/year)</th>
                                        <th style={{ padding: '8px', borderBottom: '1px solid #ccc' }}>Installation Completed</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {solarStats.map(row => (
                                        <tr key={row.propertyID}>
                                            <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                                {propertyIdToName[row.propertyID] || row.propertyID}
                                            </td>
                                            <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                                {formatNum(row.plantSize)}
                                            </td>
                                            <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                                {formatNum(row.yield)}
                                            </td>
                                            <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                                {formatNum(row.expectedEnergyGeneration)}
                                            </td>
                                            <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                                {row.installationCompleted ? row.installationCompleted.split('T')[0] : ''}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}

export default SustainabilityDashboard;